import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useParams } from "react-router";

import UserOverview from "./UserOverview";
import LoadingSpinner from "../../../common/LoadingSpinner";
import { SonicUserData } from "../../../common/classes/SonicUser";

import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  getAdminUsers,
  getUsers,
  getJikeiManager,
  updateUserChatDenialByAdmin,
} from "../../../redux/reducers/adminUsers";
import { getAuthToken } from "../../../redux/reducers/auth";
import {
  clearPersonInfo,
  getPersonAdmin,
  getPersonData,
} from "../../../redux/reducers/person";

import { PageVisibility } from "../../../utils/PageVisibility";
import { useGetTeamsAdmin } from "../../../utils/useTeam";
import { useGetProjectAdmin } from "../../../utils/useProject";
import { useGetEventAdmin } from "../../../utils/useEvent";

import styles from "../db-list/detail/DatabaseDetail.module.scss";
import { useGetTopicsAdmin } from "../../../utils/useTopics";
import { useGetFacilityAdmin } from "../../../utils/useFacility";

type Props = {
  isSelect: boolean;
  pageTitle: string;
};

const UserOverviewContainer: React.FC<Props> = ({ isSelect, pageTitle }) => {
  const { id } = useParams<{ id: string }>();
  const { pathname } = useLocation();

  const dispatch = useAppDispatch();
  const users = useAppSelector(getAdminUsers);
  const auth_token = useAppSelector(getAuthToken);
  const database_entries = useAppSelector(getPersonData);

  const [isLoading, setIsLoading] = useState(true);

  const handleUpdateDenial = async (denial: boolean) => {
    if (!auth_token || !id) return;
    setIsLoading(true);
    dispatch(
      updateUserChatDenialByAdmin({
        auth_token: auth_token,
        id: id,
        denial: denial,
      })
    ).finally(() => setIsLoading(false));
  };

  useEffect(() => {
    dispatch(clearPersonInfo());

    if (auth_token) {
      if (isSelect) {
        const fetchUsers = dispatch(
          getUsers({
            auth_token: auth_token,
            isSelect: true,
            params: { id: id },
          })
        );

        fetchUsers.finally(() => setIsLoading(false));

        return () => {
          fetchUsers.abort();
        };
      } else if (pathname.includes("jikei/student")) {
        const fetchStudents = dispatch(
          getUsers({
            auth_token: auth_token,
            isSelect: false,
            params: { id: id },
          })
        );

        fetchStudents.finally(() => setIsLoading(false));

        return () => {
          fetchStudents.abort();
        };
      } else {
        const fetchManagers = dispatch(
          getJikeiManager({ auth_token: auth_token, params: { id: id } })
        );

        fetchManagers.finally(() => setIsLoading(false));

        return () => {
          fetchManagers.abort();
        };
      }
    }
  }, []);

  useEffect(() => {
    if (auth_token && users[0]?.id == id) {
      if (users[0].meta.linked_pages.database_entry) {
        dispatch(
          getPersonAdmin({
            auth_token: auth_token,
            is_jikei_request: isSelect ? false : true,
            id: users[0].meta.linked_pages.database_entry,
            per: -1,
            status: PageVisibility.PubliclyVisible,
          })
        );
      }
    }
  }, [users]);

  const FetcContainer: React.FC<{ user: SonicUserData }> = ({ user }) => {
    const {
      items: { teams },
      isLoading: teamLoading,
    } = useGetTeamsAdmin({
      auth_token,
      is_jikei_request: !isSelect,
      id: user.meta.linked_pages.team ?? null,
      per: -1,
      status: PageVisibility.PubliclyVisible,
    });

    const {
      items: { projects },
      isLoading: projectLoading,
    } = useGetProjectAdmin(
      {
        auth_token: auth_token,
        is_jikei_request: !isSelect,
        id: users[0].meta.linked_pages.project ?? null,
        per: -1,
        status: PageVisibility.PubliclyVisible,
      },
      auth_token && users[0].meta.linked_pages.project?.length ? true : false
    );

    const {
      items: { events },
      isLoading: eventLoading,
    } = useGetEventAdmin({
      auth_token: auth_token,
      is_jikei_request: !isSelect,
      id: user.meta.linked_pages.event ?? null,
      per: -1,
      status: PageVisibility.PubliclyVisible,
    });

    // トピックスと施設は滋慶管理画面に無いため分岐処理を追加しています
    const {
      topicsData: { items: topics },
      isLoading: topicsLoading,
    } = isSelect
      ? useGetTopicsAdmin({
          auth_token: auth_token,
          id: user.meta.linked_pages.topics ?? null,
          per: -1,
          status: PageVisibility.PubliclyVisible,
        })
      : { topicsData: { items: [] }, isLoading: false };

    const {
      items: { facilities },
      isLoading: facilityLoading,
    } = isSelect
      ? useGetFacilityAdmin({
          authToken: auth_token,
          per: -1,
          status: PageVisibility.PubliclyVisible,
          owner: user.id,
        })
      : { items: { facilities: [] }, isLoading: false };

    if (
      teamLoading ||
      projectLoading ||
      eventLoading ||
      topicsLoading ||
      facilityLoading
    )
      return <LoadingSpinner space={1} />;

    return (
      <UserOverview
        user={user}
        database_entries={database_entries}
        teams={teams}
        projects={projects}
        events={events}
        topics={topics}
        facilities={facilities}
        isSelect={isSelect}
        pageTitle={pageTitle}
        handleUpdateDenial={handleUpdateDenial}
      />
    );
  };

  return (
    <div className={styles["f-databaseDetail"]}>
      {isLoading ? (
        <LoadingSpinner space={1} />
      ) : users[0] ? (
        <FetcContainer user={users[0]} />
      ) : null}
    </div>
  );
};

export default UserOverviewContainer;
